import { useDispatch } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetch3, fetch4, fetch5, fetch8 } from "../commonApis/commonApis";
import { extractErrorMessage } from "../utilities/apiErrorHandler";

const url = process.env.REACT_APP_BASE_URL;

interface ApiResponse {
  success: boolean;
  data: any;
  message?: string;
  statusCode?: number;
  statusMessage?: string;
  error?: any;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface OperationsResponse {
  success: boolean;
  data: any[];
  pagination: PaginationData;
}

// ─── Common Fetch Order Operations Hook ─────────────────────────────────────────────

export const useFetchOrderOperations = (
  status?: string,
  page: number = 1,
  limit: number = 10,
  searchFor: string = "",
  additionalFilters?: any[],
  options?: any
) => {
  return useQuery({
    queryKey: ["orderOperations", status, page, limit, searchFor, additionalFilters],
    queryFn: async (): Promise<OperationsResponse> => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        searchFor: searchFor || "",
      });

      // Add status filter if provided
      if (status) {
        params.append(`filters[0][orderStatus]`, status);
      }

      // Add additional filters if provided
      if (additionalFilters && Array.isArray(additionalFilters)) {
        const startIndex = status ? 1 : 0; // Start from index 1 if status is already added
        additionalFilters.forEach((filter, index) => {
          Object.entries(filter).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
              params.append(`filters[${startIndex + index}][${key}]`, value.toString());
            }
          });
        });
      }

      const res: OperationsResponse = await fetch3(`${url}/api/operations/get-orders?${params}`, "GET");
      return res;
    },
    placeholderData: (previousData) => previousData,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  }) as any;
};

// ─── Convenience Hooks for Specific Operations ───────────────────────────────────────

export const useCreateOrderOperations = (page?: number, limit?: number, searchFor?: string, filters?: any[]) => {
  return useFetchOrderOperations("NEW_ORDER", page, limit, searchFor, filters);
};

export const useOrderGeneratedOperations = (page?: number, limit?: number, searchFor?: string, filters?: any[]) => {
  return useFetchOrderOperations("ORDER_GENERATED", page, limit, searchFor, filters);
};

export const usePickListGetOrderOperations = (page?: number, limit?: number, searchFor?: string, filters?: any[]) => {
  return useFetchOrderOperations("ORDER_CREATED", page, limit, searchFor, filters);
};

export const usePackedGetOrderOperations = (page?: number, limit?: number, searchFor?: string, filters?: any[]) => {
  return useFetchOrderOperations("PACKED", page, limit, searchFor, filters);
};

export const useDispatchedOperations = (page?: number, limit?: number, searchFor?: string, filters?: any[]) => {
  return useFetchOrderOperations("DISPATCHED", page, limit, searchFor, filters);
};

export const useDeliveryManifestOperations = (page?: number, limit?: number, searchFor?: string, filters?: any[]) => {
  return useFetchOrderOperations("OUT_FOR_DELIVERY", page, limit, searchFor, filters);
};

// ─── Create Order API ─────────────────────────────────────────────────────────

export const useCreateOrderMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orderData: any) => {
      const res: ApiResponse = await fetch4(`${url}/api/operations/create-order`, orderData);
      return res;
    },
    onSuccess: (data) => {
      console.log("Order created successfully:", data);
      queryClient.invalidateQueries({ queryKey: ["orderOperations"] });
    },
    onError: (error) => {
      console.error("Error creating order:", error);
    },
  });
};

export const useBulkUpdateStatusMutation = () => {
  return useMutation({
    mutationFn: async (payload: { 
      identifiers: string[]; 
      targetStatus: string;
      type?: string;
      assignedTo?: string;
      updateToPicked?: boolean;
    }) => {
      const res: ApiResponse = await fetch4(`${url}/api/operations/bulk-update-status`, payload);
      return res;
    }
  });
};
